'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { CheckCircle2, FileCheck, Shield, Award } from 'lucide-react';

const complianceItems = [
  {
    title: 'FDA Food Safety Modernization Act (FSMA)',
    description: 'Full compliance with preventive controls and food safety plans.',
    icon: Shield,
  },
  {
    title: 'USDA Guidelines',
    description: 'Adherence to USDA standards for food handling and storage.',
    icon: Award,
  },
  {
    title: 'HACCP Principles',
    description: 'Hazard Analysis Critical Control Points implementation.',
    icon: FileCheck,
  },
  {
    title: 'Good Manufacturing Practices (GMP)',
    description: 'Strict adherence to GMP standards for food facilities.',
    icon: CheckCircle2,
  },
];

export function FoodGradeCompliance() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
    rootMargin: '200px 0px',
  });

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">Regulatory Compliance</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Meeting Every Food Safety Standard
          </h3>
          <p className="max-w-3xl mx-auto text-gray-700 text-lg">
            Our food grade warehouse maintains strict compliance with all federal and industry regulations, ensuring your products are stored and handled according to the highest safety standards.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {complianceItems.map((item, index) => (
            <motion.div
              key={index}
              ref={ref}
              initial="hidden"
              animate={inView ? "show" : "hidden"}
              variants={fadeIn('up', 0.1 * index)}
              className="flex items-start gap-6 p-8 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-200 transition-colors"
            >
              <div className="w-16 h-16 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
                <item.icon size={28} />
              </div>
              <div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h4>
                <p className="text-gray-700">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="bg-brand-50 border border-brand-100 rounded-2xl p-12 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Certified & Audited Regularly
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Our facility undergoes regular third-party audits and maintains all necessary certifications to ensure continuous compliance with food safety regulations. We provide complete documentation and traceability for all stored products.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-brand-600" size={16} />
                Third-Party Audited
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-brand-600" size={16} />
                Complete Documentation
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-brand-600" size={16} />
                Full Traceability
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="text-brand-600" size={16} />
                Continuous Monitoring
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}